import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { fetchOrganizations, type OrganizationItem } from '../api/organizations';
import { useActiveFeatureFlags } from '../hooks/useActiveFeatureFlags';
import { canAccessAdminArea, canCreateProject, isSuperAdminUser } from '../utils/roles';
import {
HouseFill,
BarChartFill,
GraphUpArrow,
CardChecklist,
PeopleFill,
GearFill,
PlusLg,
LayoutSidebar,
XLg,
BuildingFill,
ChevronExpand,
PlusCircle,
CreditCard2FrontFill,
PlayCircleFill,
ChevronLeft,
FileEarmarkTextFill,
ClipboardCheckFill,
LightbulbFill,
DatabaseFill,
CodeSlash,
EnvelopePaperFill,
QuestionCircleFill,
FileEarmarkCode,
Search,
Sliders2Vertical,
} from 'react-bootstrap-icons';
import { useGuidedTour } from '../tour/GuidedTourContext';
import './Sidebar-new.css';

/* ============================================================
   Sidebar-new.tsx
   Collapsible left sidebar navigation.
   Collapsed = icon-only (60 px).  Expanded = icon + label (260 px).
   ============================================================ */

/* ---------- Type definitions ---------- */

/* Props for Sidebar component */
interface SidebarProps {
    /* Whether the sidebar is in collapsed (icon-only) mode */
    collapsed: boolean;
    /* Callback to toggle collapsed state */
    onToggle: () => void;
    /* Mobile renders the sidebar as an expanded off-canvas drawer. */
    mobile?: boolean;
}

/* Shape of a single navigation entry */
interface SidebarNavEntry {
    /* Display label */
    label: string;
    /* Route path */
    to: string;
    /* React-bootstrap-icon element */
    icon: React.ReactNode;
    /* Only show when the user is admin */
    adminOnly?: boolean;
    /* Only show when the user is super admin */
    superAdminOnly?: boolean;
    /* Hide for super admins (e.g. billing screen — super admins don't subscribe) */
    hideForSuperAdmin?: boolean;
    /* A matcher to highlight this item on sub-routes */
    isActiveMatcher?: (pathname: string) => boolean;
}

/* ---------- Navigation entries ---------- */

/* Build the ordered list of sidebar nav items */
function buildNavEntries(isSuperAdmin: boolean): SidebarNavEntry[] {
    const baseEntries: SidebarNavEntry[] = [
        {
            label: 'Dashboard',
            to: '/dashboard',
            icon: <BarChartFill className="sidebar-nav-icon" />
        },
        {
            label: 'Exploratory Agent',
            to: '/exploratory_agent',
            icon: <Search className="sidebar-nav-icon" />
        },
        {
            label: 'Ritz Reservation Demo',
            to: '/ritz-reservation-demo',
            icon: <PlayCircleFill className="sidebar-nav-icon" />
        },
        {
            label: 'Analytics',
            to: '/admin/analytics',
            icon: <GraphUpArrow className="sidebar-nav-icon" />,
            adminOnly: true
        },
        {
            label: 'Projects',
            to: '/projects',
            icon: <HouseFill className="sidebar-nav-icon" />,
            /* Highlight on any /projects/* sub-route */
            isActiveMatcher: (pathname: string) => {
                return pathname === '/projects' || pathname.startsWith('/projects/');
            }
        },
        {
            label: 'Users',
            to: '/admin/users',
            icon: <PeopleFill className="sidebar-nav-icon" />,
            adminOnly: true
        },
        {
            label: 'Organizations',
            to: '/admin/organizations',
            icon: <BuildingFill className="sidebar-nav-icon" />,
            adminOnly: true,
            isActiveMatcher: (pathname: string) => {
                return pathname === '/admin/organizations' || pathname.startsWith('/admin/organizations/');
            }
        },
        {
            label: 'Config',
            to: '/admin/config',
            icon: <GearFill className="sidebar-nav-icon" />,
            adminOnly: true
        },
        {
            label: 'Platform Config',
            to: '/admin/platform-settings',
            icon: <Sliders2Vertical className="sidebar-nav-icon" />,
            superAdminOnly: true
        },
        {
            label: 'Early Access',
            to: '/admin/early-access',
            icon: <EnvelopePaperFill className="sidebar-nav-icon" />,
            superAdminOnly: true
        },
        {
            label: 'Feedback',
            to: '/admin/feedback',
            icon: <LightbulbFill className="sidebar-nav-icon" />,
            superAdminOnly: true
        },
        {
            label: 'Support Tickets',
            to: '/admin/support-tickets',
            icon: <QuestionCircleFill className="sidebar-nav-icon" />,
            superAdminOnly: true
        },
        {
            label: 'Subscription Plans',
            to: '/admin/subscription-plans',
            icon: <CardChecklist className="sidebar-nav-icon" />,
            superAdminOnly: true
        },
        {
            label: 'Organization Billing',
            to: '/admin/org-billing',
            icon: <CreditCard2FrontFill className="sidebar-nav-icon" />,
            superAdminOnly: true
        },
        {
            label: 'Model Catalogue',
            to: '/admin/llm-models',
            icon: <DatabaseFill className="sidebar-nav-icon" />,
            superAdminOnly: true
        },
        {
            label: 'Billing',
            to: '/billing',
            icon: <CreditCard2FrontFill className="sidebar-nav-icon" />,
            adminOnly: true,
            hideForSuperAdmin: true,
            isActiveMatcher: (pathname: string) => pathname === '/billing' || pathname === '/subscription-billing',
        },
        {
            label: 'Support Tickets',
            to: '/support-tickets',
            icon: <QuestionCircleFill className="sidebar-nav-icon" />,
            adminOnly: true,
            hideForSuperAdmin: true,
        }
    ];

    return baseEntries;
}

/* ---------- Component: Sidebar ---------- */

function Sidebar({ collapsed, onToggle, mobile = false }: SidebarProps): React.ReactElement | null {
    /* Auth & routing hooks */
    const { user, isAuthenticated, selectOrganization, clearOrganizationSelection } = useAuth();
    const { projects } = useProjects();
    const { startGuidedTour, isGuidedTourAvailable } = useGuidedTour();
    const { flags } = useActiveFeatureFlags();
    const location = useLocation();
    const navigate = useNavigate();

    /* Organization dropdown state */
    const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
    const [organizations, setOrganizations] = useState<OrganizationItem[]>([]);
    const orgDropdownRef = useRef<HTMLDivElement>(null);

    /* Public pages: hide the sidebar entirely */
    const publicPaths = ['/', '/login', '/register', '/early-access'];
    const isPublic = publicPaths.includes(location.pathname);

    /* Is the current user an admin? */
    const isAdmin = canAccessAdminArea(user);
    const isSuperAdmin = isSuperAdminUser(user);
    const showOrganizationSelector = true;
    const currentOrganizationId = user?.active_organization_id || user?.organization_id;
    const organizationName = (user?.organization_name || '').trim() || 'Select Organization';
    const canCreateProjectForUser = canCreateProject(user);

    /* Fetch organizations when dropdown opens */
    useEffect(() => {
        if (!orgDropdownOpen || !isAuthenticated || !showOrganizationSelector) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchOrganizations(1, 50, false, undefined, {
                    ignoreActiveOrganizationScope: true,
                });
                if (!cancelled) {
                    setOrganizations(res.items);
                }
            }
            catch {
                /* Silently ignore — user will see empty dropdown */
            }
        })();
        return () => { cancelled = true; };
    }, [orgDropdownOpen, isAuthenticated, showOrganizationSelector]);

    /* Close dropdown when clicking outside */
    const handleClickOutside = useCallback((e: MouseEvent) => {
        if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target as Node)) {
            setOrgDropdownOpen(false);
        }
    }, []);

    useEffect(() => {
        if (orgDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }, [orgDropdownOpen, handleClickOutside]);

    /* Extract projectId from current path if inside a project — must be before early returns */
    const currentProjectId = useMemo(() => {
        const match = location.pathname.match(/^\/projects\/(\d+)/);
        return match ? match[1] : null;
    }, [location.pathname]);
    const isBillingContext = location.pathname === '/billing'
        || location.pathname === '/billing/payment-history'
        || location.pathname === '/admin/org-billing'
        || location.pathname === '/admin/payment-history';

    if (isPublic) return null;
    if (!isAuthenticated) return null;

    /* Build nav entries and filter admin-only when needed */
    let navEntries = buildNavEntries(isSuperAdmin).filter((entry) => {
        if (entry.superAdminOnly && !isSuperAdmin) {
            return false;
        }
        if (entry.adminOnly && !isAdmin) {
            return false;
        }
        if (entry.hideForSuperAdmin && isSuperAdmin) {
            return false;
        }
        return true;
    });

    if (flags.data_validation_agent) {
        const projectsIndex = navEntries.findIndex((e) => e.label === 'Projects');
        const dvEntry: SidebarNavEntry = {
            label: 'Data Validation',
            to: '/data-validation',
            icon: <ClipboardCheckFill className="sidebar-nav-icon" />,
            isActiveMatcher: (pathname: string) => pathname === '/data-validation' || pathname.startsWith('/data-validation/'),
        };
        if (projectsIndex >= 0) {
            navEntries = [
                ...navEntries.slice(0, projectsIndex + 1),
                dvEntry,
                ...navEntries.slice(projectsIndex + 1),
            ];
        } else {
            navEntries = [dvEntry, ...navEntries];
        }
    }

    navEntries = navEntries.filter((entry) => {
        if (entry.label === 'Exploratory Agent') return flags.exploratory_agent;
        if (entry.label === 'Ritz Reservation Demo') return flags.ritz_reservation_demo;
        return true;
    });

    /* ---------- Event handlers ---------- */

    /* Toggle collapsed / expanded sidebar */
    function handleToggleCollapse(): void {
        onToggle();
    }

    /* Navigate to create-project page */
    function handleCreateProject(): void {
        if (!canCreateProjectForUser) {
            return;
        }
        navigate('/projects/new');
    }

    function handleStartGuidedTour(): void {
        startGuidedTour();
    }

    /* ---------- Helpers ---------- */

    /* Decide if a nav entry is currently active */
    function isNavActive(entry: SidebarNavEntry): boolean {
        if (entry.isActiveMatcher) {
            return entry.isActiveMatcher(location.pathname);
        }
        return location.pathname === entry.to;
    }

    function renderHelpButton(): React.ReactNode {
        if (!isGuidedTourAvailable) {
            return null;
        }

        return (
            <button
                id="sidebar-help-tour"
                className="sidebar-nav-item sidebar-help-btn"
                title="Help"
                type="button"
                onClick={handleStartGuidedTour}
            >
                <QuestionCircleFill className="sidebar-nav-icon" />
                <span className="sidebar-nav-label">Help</span>
            </button>
        );
    }

    /* ---------- Render helpers ---------- */

    /* Render the sidebar header with brand + toggle */
    function renderHeader(): React.ReactNode {
        return (
            <div className="sidebar-header">
                {/* Brand block — visible only when expanded */}
                <div className="sidebar-brand">
                    <img
                        src="/Accelirate.svg"
                        alt="AcceliQA Logo"
                        className="sidebar-brand-logo"
                    />
                    <span className="sidebar-brand-text">AcceliQA</span>
                </div>

                {/* Collapse / expand toggle */}
                <button
                    className="sidebar-toggle-btn"
                    onClick={handleToggleCollapse}
                    title={mobile ? 'Close navigation' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    aria-label={mobile ? 'Close navigation' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    type="button"
                    autoFocus={mobile}
                >
                    {mobile ? <XLg size={18} /> : <LayoutSidebar size={18} />}
                </button>
            </div>
        );
    }

    /* Render the Create Project button */
    function renderCreateButton(): React.ReactNode {
        if (!canCreateProjectForUser) {
            return null;
        }

        return (
            <button
                className="sidebar-create-btn"
                onClick={handleCreateProject}
                title="Create Project"
                type="button"
            >
                <PlusLg className="sidebar-nav-icon" />
                <span className="sidebar-nav-label">Create Project</span>
            </button>
        );
    }

    /* Render a single nav entry */
    function renderNavItem(entry: SidebarNavEntry): React.ReactNode {
        const active = isNavActive(entry);

        return (
            <NavLink
                key={entry.to}
                to={entry.to}
                className={() => `sidebar-nav-item ${active ? 'sidebar-nav-active' : ''}`}
                title={entry.label}
                end={!entry.isActiveMatcher}
            >
                {entry.icon}
                <span className="sidebar-nav-label">{entry.label}</span>
            </NavLink>
        );
    }

    /* Render the navigation items list */
    function renderNavItems(): React.ReactNode {
        /* When inside a project, show project-focused nav */
        if (currentProjectId) {
            return renderProjectNavItems(currentProjectId);
        }
        if (isBillingContext) {
            return renderBillingNavItems();
        }

        return (
            <nav className="sidebar-nav">
                {/* Create Project button at the top */}
                {renderCreateButton()}

                {/* Navigation entries */}
                {navEntries.map((entry) => {
                    return renderNavItem(entry);
                })}

                {renderHelpButton()}
            </nav>
        );
    }

    function renderBillingNavItems(): React.ReactNode {
        const isSuperAdminBilling = isSuperAdmin && location.pathname.startsWith('/admin/');
        const overviewPath = isSuperAdminBilling ? '/admin/org-billing' : '/billing';
        const historyPath = isSuperAdminBilling ? '/admin/payment-history' : '/billing/payment-history';
        const entries = [
            { label: 'Overview', to: overviewPath, icon: <CreditCard2FrontFill className="sidebar-nav-icon" /> },
            { label: 'Payment History', to: historyPath, icon: <FileEarmarkTextFill className="sidebar-nav-icon" /> },
        ];

        return (
            <nav className="sidebar-nav">
                <NavLink to={isSuperAdminBilling ? '/admin/organizations' : '/dashboard'} className={() => 'sidebar-nav-item sidebar-back-item'} title={isSuperAdminBilling ? 'Back to Administration' : 'Back to Dashboard'}>
                    <ChevronLeft className="sidebar-nav-icon" />
                    <span className="sidebar-nav-label">{isSuperAdminBilling ? 'Back to Administration' : 'Back to Dashboard'}</span>
                </NavLink>
                <div className="sidebar-project-section">
                    <span className="sidebar-project-section-label sidebar-nav-label">Billing</span>
                </div>
                {entries.map((entry) => (
                    <NavLink
                        key={entry.to}
                        to={entry.to}
                        className={() => `sidebar-nav-item ${location.pathname === entry.to ? 'sidebar-nav-active' : ''}`}
                        title={entry.label}
                    >
                        {entry.icon}
                        <span className="sidebar-nav-label">{entry.label}</span>
                    </NavLink>
                ))}
                {renderHelpButton()}
            </nav>
        );
    }

    /* Render project-scoped navigation items */
    function renderProjectNavItems(projectId: string): React.ReactNode {
        const pathname = location.pathname;
        const search = location.search;
        const base = `/projects/${projectId}`;

        /* Look up project name from context */
        const currentProject = projects.find((p) => String(p.id) === projectId);
        const projectName = currentProject?.name || 'Project';

        type ProjectEntry = {
            label: string;
            to: string;
            icon: React.ReactNode;
            isActive: boolean;
        };

        const projectEntries: ProjectEntry[] = [
            {
                label: 'Requirements Workspace',
                to: `${base}?tab=requirements`,
                icon: <FileEarmarkTextFill className="sidebar-nav-icon" />,
                isActive: pathname === base && search.includes('tab=requirements'),
            },
            {
                label: 'Test Cases',
                to: `${base}?tab=test-cases`,
                icon: <ClipboardCheckFill className="sidebar-nav-icon" />,
                isActive: pathname === base && (search.includes('tab=test-cases') || !search.includes('tab=')),
            },
            {
                label: 'Test Executions',
                to: `${base}?tab=executions`,
                icon: <PlayCircleFill className="sidebar-nav-icon" />,
                isActive: pathname === base && search.includes('tab=executions'),
            },
            {
                label: 'Knowledge',
                to: `${base}?tab=domain`,
                icon: <LightbulbFill className="sidebar-nav-icon" />,
                isActive: pathname === base && search.includes('tab=domain'),
            },
            {
                label: 'Analytics',
                to: `${base}?tab=analytics`,
                icon: <BarChartFill className="sidebar-nav-icon" />,
                isActive: pathname === base && search.includes('tab=analytics'),
            },
            {
                label: 'Generated Datasets',
                to: `${base}/test-data`,
                icon: <DatabaseFill className="sidebar-nav-icon" />,
                isActive: pathname.startsWith(`${base}/test-data`),
            },
            {
                label: 'Automated Tests',
                to: `${base}/automated-tests`,
                icon: <CodeSlash className="sidebar-nav-icon" />,
                isActive: pathname.startsWith(`${base}/automated-tests`),
            },
            {
                label: 'Gherkin / BDD',
                to: `${base}?tab=gherkin`,
                icon: <FileEarmarkCode className="sidebar-nav-icon" />,
                isActive: pathname === base && search.includes('tab=gherkin'),
            },
            {
                label: 'Jobs',
                to: `${base}?tab=jobs`,
                icon: <CardChecklist className="sidebar-nav-icon" />,
                isActive: pathname === base && search.includes('tab=jobs'),
            },
        ];

        const filteredProjectEntries = projectEntries.filter((entry) => {
            if (entry.label === 'Test Executions' || entry.label === 'Automated Tests') {
                return !!flags.test_execution;
            }
            if (entry.label === 'Generated Datasets') {
                return !!flags.synthetic_data;
            }
            return true;
        });

        return (
            <nav className="sidebar-nav">
                {/* Back to all projects */}
                <NavLink
                    to="/projects"
                    className={() => 'sidebar-nav-item sidebar-back-item'}
                    title="All Projects"
                    end
                >
                    <ChevronLeft className="sidebar-nav-icon" />
                    <span className="sidebar-nav-label">All Projects</span>
                </NavLink>

                {/* Section label — shows project name */}
                <div className="sidebar-project-section">
                    <span className="sidebar-project-section-label sidebar-nav-label">{projectName}</span>
                </div>

                {/* Project nav entries */}
                {filteredProjectEntries.map((entry) => (
                    <NavLink
                        key={entry.to}
                        to={entry.to}
                        className={() =>
                            `sidebar-nav-item ${entry.isActive ? 'sidebar-nav-active' : ''}`
                        }
                        title={entry.label}
                    >
                        {entry.icon}
                        <span className="sidebar-nav-label">{entry.label}</span>
                    </NavLink>
                ))}

                {renderHelpButton()}
            </nav>
        );
    }

    /* Render the organization section at the bottom */
    function renderFooter(): React.ReactNode {
        /* Toggle dropdown open / closed */
        function handleToggleOrgDropdown(): void {
            setOrgDropdownOpen((prev) => !prev);
        }

        /* Handle clicking an organization in the dropdown */
        function handleSelectOrg(org: OrganizationItem): void {
            selectOrganization(org.id, { id: org.id, name: org.name });
            setOrgDropdownOpen(false);
        }

        function handleClearOrgSelection(): void {
            clearOrganizationSelection();
            setOrgDropdownOpen(false);
        }

        /* Handle "Add New Organization" click */
        function handleAddOrg(): void {
            setOrgDropdownOpen(false);
            navigate('/admin/organizations');
        }

        if (!showOrganizationSelector) {
            return null;
        }

        return (
            <div className="sidebar-footer" ref={orgDropdownRef}>
                {/* Dropdown menu — positioned above the trigger */}
                {orgDropdownOpen && (
                    <div className={`sidebar-org-dropdown ${collapsed ? 'sidebar-org-dropdown-collapsed' : ''}`}>
                        {/* Organization list */}
                        <div className="sidebar-org-dropdown-list">
                            {isSuperAdmin && (
                                <button
                                    className={`sidebar-org-dropdown-item ${currentOrganizationId == null ? 'sidebar-org-dropdown-item-active' : ''}`}
                                    type="button"
                                    onClick={handleClearOrgSelection}
                                >
                                    <span className="sidebar-org-dropdown-item-icon-wrap">
                                        <BuildingFill className="sidebar-org-dropdown-icon" size={18} />
                                    </span>
                                    <span className="sidebar-org-dropdown-name">No Active Organization</span>
                                </button>
                            )}
                            {organizations.map((org) => {
                                const isActiveOrganization = currentOrganizationId != null
                                    ? org.id === currentOrganizationId
                                    : org.name.trim().toLowerCase() === organizationName.toLowerCase();

                                return (
                                    <button
                                        key={org.id}
                                        className={`sidebar-org-dropdown-item ${isActiveOrganization ? 'sidebar-org-dropdown-item-active' : ''}`}
                                        type="button"
                                        onClick={() => handleSelectOrg(org)}
                                    >
                                        <span className="sidebar-org-dropdown-item-icon-wrap">
                                            <BuildingFill className="sidebar-org-dropdown-icon" size={18} />
                                        </span>
                                        <span className="sidebar-org-dropdown-name">{org.name}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Add New Organization */}
                        {isAdmin && (
                            <button
                                className="sidebar-org-dropdown-add"
                                type="button"
                                onClick={handleAddOrg}
                            >
                                <PlusCircle className="sidebar-org-dropdown-add-icon" size={18} />
                                <span>Add New Organization</span>
                            </button>
                        )}
                    </div>
                )}

                {/* Trigger button */}
                <button
                    className="sidebar-org-selector"
                    onClick={handleToggleOrgDropdown}
                    type="button"
                    title="Organization"
                >
                    <span className="sidebar-org-icon">
                        <BuildingFill size={16} />
                    </span>
                    <span className="sidebar-org-trigger-copy">
                        <span className="sidebar-org-name">{organizationName}</span>
                    </span>
                    <span className="sidebar-org-chevron">
                        <ChevronExpand size={12} />
                    </span>
                </button>
            </div>
        );
    }

    /* ---------- Main render ---------- */

    /* CSS class based on collapsed state */
    const sidebarClass = `${collapsed ? 'sidebar sidebar-collapsed' : 'sidebar sidebar-expanded'}${mobile ? ' sidebar-mobile' : ''}`;

    return (
        <aside id="app-primary-navigation" className={sidebarClass} aria-label="Primary navigation">
            {renderHeader()}
            {renderNavItems()}
            {renderFooter()}
        </aside>
    );
}

/* ---------- Export ---------- */

export default Sidebar;
